import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { ParticleKind } from '../../art/sceneFx';

interface KindSpec {
  color: string;
  color2?: string;
  vel: [number, number, number];
  sway: number;
  size: number;
  alpha: number;
  streak?: boolean;
  twinkle: number;
  additive: boolean;
  share: number;
}

const KINDS: Record<ParticleKind, KindSpec> = {
  embers: { color: '#ffae4a', color2: '#ff6a1f', vel: [0.05, 0.42, 0], sway: 0.35, size: 10, alpha: 0.95, twinkle: 6, additive: true, share: 0.35 },
  dust: { color: '#ffe6bf', vel: [0.06, -0.03, 0], sway: 0.25, size: 5, alpha: 0.45, twinkle: 1, additive: true, share: 0.4 },
  motes: { color: '#fff4d8', vel: [0.02, 0.05, 0], sway: 0.15, size: 3.5, alpha: 0.8, twinkle: 3, additive: true, share: 0.3 },
  sparks: { color: '#ff7ae0', color2: '#ffd36a', vel: [0.0, 0.25, 0], sway: 0.9, size: 8, alpha: 1, twinkle: 12, additive: true, share: 0.35 },
  rain: { color: '#c3d0ff', vel: [-0.9, -9, 0], sway: 0, size: 16, alpha: 0.5, streak: true, twinkle: 0, additive: true, share: 0.7 },
  ash: { color: '#ff5a3a', color2: '#5a1010', vel: [0.08, -0.22, 0], sway: 0.4, size: 7, alpha: 0.8, twinkle: 4, additive: true, share: 0.4 },
};

const vertex = /* glsl */ `
attribute vec4 aSeed;
uniform float uTime;
uniform vec3 uVel;
uniform float uSway;
uniform float uSize;
uniform float uPixelRatio;
uniform float uTwinkle;
uniform float uMotion;
varying float vTw;
varying float vMix;
void main() {
  vec3 box = vec3(13.0, 8.0, 11.0);
  vec3 p = aSeed.xyz * box - vec3(box.x * 0.5, box.y * 0.5, box.z + 2.0);
  float t = uTime * uMotion;
  p += uVel * t * (0.6 + aSeed.w * 0.8);
  p.x += sin(t * 0.7 + aSeed.w * 40.0) * uSway;
  p.y += cos(t * 0.5 + aSeed.w * 23.0) * uSway * 0.4;
  // wrap inside the box
  p.x = mod(p.x + box.x * 0.5, box.x) - box.x * 0.5;
  p.y = mod(p.y + box.y * 0.5, box.y) - box.y * 0.5;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_PointSize = uSize * uPixelRatio * (0.5 + aSeed.w) * (4.0 / max(0.5, -mv.z));
  vTw = 0.55 + 0.45 * sin(t * uTwinkle + aSeed.w * 90.0);
  vMix = aSeed.w;
}
`;

const fragment = /* glsl */ `
uniform vec3 uColor;
uniform vec3 uColor2;
uniform float uAlpha;
uniform float uStreak;
uniform float uLight;
varying float vTw;
varying float vMix;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  float a;
  if (uStreak > 0.5) {
    a = smoothstep(0.08, 0.0, abs(c.x)) * smoothstep(0.5, 0.1, abs(c.y));
  } else {
    a = smoothstep(0.5, 0.0, length(c));
    a *= a;
  }
  vec3 col = mix(uColor, uColor2, vMix);
  gl_FragColor = vec4(col * uLight, a * uAlpha * vTw);
  #include <colorspace_fragment>
}
`;

interface Props {
  kind: ParticleKind;
  count: number;
  reducedMotion: boolean;
  light: { current: number };
}

export function Particles({ kind, count, reducedMotion, light }: Props) {
  const spec = KINDS[kind];
  const n = Math.max(8, Math.round(count * spec.share));
  const ref = useRef<THREE.Points>(null);
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const seeds = new Float32Array(n * 4);
    for (let i = 0; i < n * 4; i++) seeds[i] = Math.random();
    g.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 4));
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, -7), 20);
    return g;
  }, [n]);
  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uVel: { value: new THREE.Vector3(...spec.vel) },
          uSway: { value: spec.sway },
          uSize: { value: spec.size },
          uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
          uTwinkle: { value: spec.twinkle },
          uMotion: { value: 1 },
          uColor: { value: new THREE.Color(spec.color) },
          uColor2: { value: new THREE.Color(spec.color2 ?? spec.color) },
          uAlpha: { value: spec.alpha },
          uStreak: { value: spec.streak ? 1 : 0 },
          uLight: { value: 1 },
        },
        vertexShader: vertex,
        fragmentShader: fragment,
        transparent: true,
        depthWrite: false,
        blending: spec.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      }),
    [spec],
  );
  useFrame((state) => {
    material.uniforms.uTime.value = reducedMotion ? 0 : state.clock.elapsedTime;
    material.uniforms.uMotion.value = reducedMotion ? 0 : 1;
    material.uniforms.uLight.value = 0.6 + light.current * 0.5;
  });
  return <points ref={ref} geometry={geometry} material={material} frustumCulled={false} renderOrder={20} />;
}
